import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getUserActivityTimeline } from '../apiClient';

interface Activity {
  id: number;
  type: 'quiz' | 'test' | 'achievement' | 'streak';
  title: string;
  subtitle: string;
  timestamp: string;
  icon: string;
  color: string;
  details?: string;
  score?: string;
  points?: number;
}

const ActivityTimeline: React.FC = () => {
  const [recentActivities, setRecentActivities] = useState<Activity[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    fetchActivities();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchActivities = async () => {
    setIsLoading(true);
    try {
      // Fetch real activity timeline data
      const response = await getUserActivityTimeline(5);
      
      if (response && response.result && Array.isArray(response.result)) {
        // Map API response to Activity format
        const activities: Activity[] = response.result.map((activity: any, index: number) => ({
          id: activity.quiz_id || index,
          type: activity.type || 'quiz',
          title: activity.title,
          subtitle: activity.subtitle,
          timestamp: formatTimestamp(activity.timestamp),
          icon: getIconForType(activity.type),
          color: getColorForType(activity.type),
          details: activity.details,
          score: activity.score,
          points: activity.points || 0
        }));
        
        setRecentActivities(activities);
      } else {
        // Fallback to empty array if no activities
        setRecentActivities([]);
      }
    } catch (error) {
      console.error('Failed to fetch activities:', error);
      // Show empty state on error
      setRecentActivities([]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatTimestamp = (timestamp: string): string => {
    if (!timestamp) return '';
    
    const activityDate = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - activityDate.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return 'Today, ' + activityDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday, ' + activityDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays < 7) {
      return diffDays + ' days ago';
    } else {
      return activityDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const getIconForType = (type: string): string => {
    switch (type) {
      case 'quiz': return 'fas fa-clipboard-check';
      case 'test': return 'fas fa-file-alt';
      case 'achievement': return 'fas fa-trophy';
      case 'streak': return 'fas fa-fire';
      default: return 'fas fa-check-circle';
    }
  };

  const getColorForType = (type: string): string => {
    switch (type) {
      case 'quiz': return '#27ae60';
      case 'test': return '#2d9cdb';
      case 'achievement': return '#f39c12';
      case 'streak': return '#e74c3c';
      default: return '#667eea';
    }
  };

  const getActivityIcon = (activity: Activity) => {
    return (
      <div className="activity-icon rounded-circle d-flex align-items-center justify-content-center"
        style={{
          width: '50px',
          height: '50px',
          background: activity.color + '15',
          color: activity.color,
          flexShrink: 0
        }}>
        <i className={activity.icon}></i>
      </div>
    );
  };

  return (
    <div className="activity-timeline-section mb-4">
      <div className="card border-0 shadow-sm" style={{ borderRadius: '20px' }}>
        <div 
          className="card-header bg-white border-0 p-3" 
          onClick={() => setIsExpanded(!isExpanded)}
          style={{ cursor: 'pointer' }}
        >
          {!isExpanded ? (
            <div className="d-flex align-items-center">
              <div 
                style={{
                  width: '48px',
                  height: '48px',
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '12px',
                  flexShrink: 0
                }}
              >
                <i className="fas fa-history" style={{ fontSize: '24px', color: 'white' }}></i>
              </div>
              <div className="flex-grow-1">
                <h5 className="mb-0 text-dark" style={{ fontSize: '16px', fontWeight: '600' }}>Recent Activity</h5>
              </div>
              {recentActivities.length > 0 && (
                <span className="badge bg-primary rounded-pill px-3 py-1 me-2" style={{ fontSize: '11px' }}>
                  {recentActivities.length} activities
                </span>
              )}
              <div 
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  background: '#e8eaf6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <i className="fas fa-chevron-down" style={{ color: '#667eea', fontSize: '11px' }}></i>
              </div>
            </div>
          ) : (
            <div className="d-flex align-items-center">
              <div 
                style={{
                  width: '48px',
                  height: '48px',
                  background: 'linear-gradient(135deg, #667eea, #764ba2)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '12px',
                  flexShrink: 0
                }}
              >
                <i className="fas fa-history" style={{ fontSize: '24px', color: 'white' }}></i>
              </div>
              <div className="flex-grow-1">
                <h5 className="mb-0 text-dark" style={{ fontSize: '16px', fontWeight: '600' }}>Recent Activity</h5>
              </div>
              <div 
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  background: '#e8eaf6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <i className="fas fa-chevron-up" style={{ color: '#667eea', fontSize: '11px' }}></i>
              </div>
            </div>
          )}
        </div>

        {isExpanded && (
        <div className="card-body p-4 animate__animated animate__fadeIn">
          {isLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="activity-timeline">
              {recentActivities.map((activity, index) => (
                <div key={activity.id} 
                  className="activity-item d-flex align-items-start mb-4 pb-4"
                  style={{ 
                    borderBottom: index < recentActivities.length - 1 ? '1px solid #e9ecef' : 'none',
                    position: 'relative'
                  }}>
                  
                  {/* Timeline Line */}
                  {index < recentActivities.length - 1 && (
                    <div className="position-absolute"
                      style={{
                        left: '24px',
                        top: '60px',
                        bottom: '-10px',
                        width: '2px',
                        background: 'linear-gradient(to bottom, ' + activity.color + '30, transparent)',
                        zIndex: 0
                      }}></div>
                  )}

                  {/* Icon */}
                  <div className="me-3" style={{ zIndex: 1 }}>
                    {getActivityIcon(activity)}
                  </div>

                  {/* Content */}
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-start justify-content-between mb-2">
                      <div>
                        <h6 className="fw-bold mb-1">{activity.title}</h6>
                        <p className="text-muted mb-0 small">{activity.subtitle}</p>
                      </div>
                      <small className="text-muted">{activity.timestamp}</small>
                    </div>

                    {/* Details */}
                    {activity.details && (
                      <div className="mb-2">
                        <small className="text-muted">
                          <i className="fas fa-info-circle me-1"></i>
                          {activity.details}
                        </small>
                      </div>
                    )}

                    {/* Stats */}
                    <div className="d-flex flex-wrap gap-2">
                      {activity.score && (
                        <span className="badge rounded-pill px-3 py-2"
                          style={{
                            background: activity.color + '15',
                            color: activity.color,
                            fontSize: '12px',
                            fontWeight: 600
                          }}>
                          <i className="fas fa-star me-1"></i>
                          Score: {activity.score}
                        </span>
                      )}
                      {activity.points && (
                        <span className="badge rounded-pill px-3 py-2"
                          style={{
                            background: '#f39c1215',
                            color: '#f39c12',
                            fontSize: '12px',
                            fontWeight: 600
                          }}>
                          <i className="fas fa-coins me-1"></i>
                          +{activity.points} points
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* No Activities State */}
          {!isLoading && recentActivities.length === 0 && (
            <div className="text-center py-5">
              <i className="fas fa-inbox fa-3x text-muted mb-3"></i>
              <p className="text-muted">No recent activities yet. Start a quiz to see your progress here!</p>
              <Link to="/mock-quiz" className="btn btn-primary rounded-pill px-4">
                <i className="fas fa-play me-2"></i>
                Start a Quiz
              </Link>
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
};

export default ActivityTimeline;
